// Cliente de la app de Farmacia Interna (ver packages/web/src/api/http-
// routes/hce-farmacia.ts y lib/ventas.ts). Mismo Bearer (id de la sesión de
// proxy) que el resto de `/api/hce/*` — no el token de admin de hceComite.ts. Director
// Técnico Q.F./B.F. es un rol propio desde 2026-09-11 (antes era un
// "personal-medico" más), sin login admin_users del panel CRM — por eso este
// cliente existe en vez de reusar el de /admin/catalogo o /admin/inventario.
import { vaultUrl } from './hceVault';
import { cabecerasBoveda } from './audit/auditedFetch';

async function post<T>(path: string, body: unknown): Promise<{ ok: true; datos: T } | { ok: false; error: string }> {
  const res = await fetch(vaultUrl(path), { method: 'POST', headers: cabecerasBoveda(), body: JSON.stringify(body) });
  const datos = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: datos?.error || 'No se pudo completar la operación.' };
  return { ok: true, datos };
}

async function patch<T>(path: string, body: unknown): Promise<{ ok: true; datos: T } | { ok: false; error: string }> {
  const res = await fetch(vaultUrl(path), { method: 'PATCH', headers: cabecerasBoveda(), body: JSON.stringify(body) });
  const datos = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: datos?.error || 'No se pudo completar la operación.' };
  return { ok: true, datos };
}

async function get<T>(path: string, porDefecto: T): Promise<T> {
  const res = await fetch(vaultUrl(path), { headers: cabecerasBoveda() });
  if (!res.ok) return porDefecto;
  return res.json().catch(() => porDefecto);
}

export type StockItemFarmacia = { catalogItemId: number; nombre: string; unidad: string; disponible: number };

export async function listarStockItemsFarmacia(): Promise<StockItemFarmacia[]> {
  const body = await get<{ items?: StockItemFarmacia[] }>('/api/hce/farmacia/stock-items', {});
  return Array.isArray(body?.items) ? body.items : [];
}

export type BuyerIdTypeFarmacia = 'cedula' | 'ruc' | 'pasaporte' | 'consumidor_final';

export type PerfilFacturacion = {
  id: number;
  idType: 'cedula' | 'ruc' | 'pasaporte';
  idNumber: string;
  name: string;
  address: string | null;
  email: string | null;
  updatedAt: string;
};

/** Perfiles de facturación que este paciente ya usó antes — para autocompletar al elegirlo en "Vender". */
export async function listarPerfilesFacturacion(patientId: number): Promise<PerfilFacturacion[]> {
  const body = await get<{ perfiles?: PerfilFacturacion[] }>(`/api/hce/farmacia/clientes-facturacion/${patientId}`, {});
  return Array.isArray(body?.perfiles) ? body.perfiles : [];
}

export type CatalogoItemFarmacia = { id: number; code: string; name: string; kind: 'drug' | 'supply'; amountCents: number; active: boolean };

export async function listarCatalogoFarmacia(): Promise<CatalogoItemFarmacia[]> {
  const body = await get<{ items?: CatalogoItemFarmacia[] }>('/api/hce/farmacia/catalogo', {});
  return Array.isArray(body?.items) ? body.items : [];
}

export async function crearCatalogoFarmacia(input: { kind: 'drug' | 'supply'; code: string; name: string; amountCents: number }) {
  return post<{ id: number }>('/api/hce/farmacia/catalogo', input);
}

/** Edición (2026-09-10) — antes este catálogo solo permitía alta, así que un precio cargado mal quedaba así para siempre. */
export async function actualizarCatalogoFarmacia(id: number, cambios: { amountCents?: number; name?: string; active?: boolean }) {
  return patch<{ ok: true }>(`/api/hce/farmacia/catalogo/${id}`, cambios);
}

export type FilaImportarCatalogo = { fila: number; detalle: string };
export type ResultadoImportarCatalogo = { creados: number; fallidos: FilaImportarCatalogo[] };

/** Alta masiva desde un Excel/CSV de columnas fijas — ver el texto de ayuda en CatalogoFarmacia.tsx. `fileBase64` sin el prefijo `data:...;base64,`. */
export async function importarCatalogoFarmacia(fileBase64: string) {
  return post<ResultadoImportarCatalogo>('/api/hce/farmacia/catalogo/importar', { fileBase64 });
}

export type LoteFarmacia = { id: number; lotCode: string; expiresAt: string | null; quantity: number };
export type ItemInventarioFarmacia = {
  stockItemId: number;
  catalogItemId: number;
  code: string;
  name: string;
  unit: string;
  reorderLevel: number;
  quantity: number;
  lots: LoteFarmacia[];
};
export type MovimientoInventarioFarmacia = {
  id: number;
  lotId: number;
  lotCode: string;
  itemName: string;
  kind: 'in' | 'out' | 'adjust' | 'expire' | 'transfer_out' | 'transfer_in';
  quantity: number;
  reason: string | null;
  orderId: number | null;
  createdAt: string;
};

export async function listarInventarioFarmacia(): Promise<{ items: ItemInventarioFarmacia[]; moves: MovimientoInventarioFarmacia[] }> {
  return get('/api/hce/farmacia/inventario', { items: [], moves: [] });
}

export async function cargarLoteFarmacia(input: { catalogItemId: number; lotCode: string; quantity: number; expiresAt?: string | null; unit?: string }) {
  return post<{ lotId: number; stockItemId: number }>('/api/hce/farmacia/inventario/lote', input);
}

export async function ajustarLoteFarmacia(input: { lotId: number; quantity: number; reason?: string; kind?: 'adjust' | 'expire' }) {
  return post<{ quantity: number }>('/api/hce/farmacia/inventario/ajuste', input);
}

// =============================================================================
// Botiquín de servicio (Fase 2, ítem 12) — transferencia de stock desde
// Farmacia Interna a un espejo en el catálogo del CRM, firmada por el
// Director Técnico. Ver http-routes/hce-farmacia.ts:/botiquin/*.
// =============================================================================

export type ItemElegibleBotiquin = { farmaciaItemId: number; nombre: string; kind: 'drug' | 'supply'; unidad: string; disponible: number };

/** Ítems de farmacia con stock, drug/supply, activos y no fiscalizados — para el selector del picker de "Reponer botiquín". */
export async function listarItemsElegiblesBotiquin(): Promise<ItemElegibleBotiquin[]> {
  const body = await get<{ items?: ItemElegibleBotiquin[] }>('/api/hce/farmacia/botiquin/items-elegibles', {});
  return Array.isArray(body?.items) ? body.items : [];
}

export type LoteTransferible = { id: number; lotCode: string; expiresAt: string | null; quantity: number };

export async function listarLotesTransferibles(farmaciaItemId: number): Promise<LoteTransferible[]> {
  const body = await get<{ lotes?: LoteTransferible[] }>(`/api/hce/farmacia/botiquin/lotes-transferibles?farmaciaItemId=${farmaciaItemId}`, {});
  return Array.isArray(body?.lotes) ? body.lotes : [];
}

export type SugerenciaReposicionBotiquin = { sugerido: number | null; consumidoUltimosDias: number };

/** Cuánto se consumió del ítem espejo en el CRM en los últimos 30 días — referencia para la cantidad a transferir. `sugerido: null` si todavía no existe el espejo (primera transferencia). */
export async function sugerenciaReposicionBotiquin(farmaciaItemId: number): Promise<SugerenciaReposicionBotiquin> {
  return get(`/api/hce/farmacia/botiquin/sugerencia?farmaciaItemId=${farmaciaItemId}`, { sugerido: null, consumidoUltimosDias: 0 });
}

export type ResultadoTransferenciaBotiquin = { ok: true; espejoItemId: number; espejoLotId: number; espejoLotCode: string; quantity: number };

export async function transferirABotiquin(input: { farmaciaItemId: number; lotId: number; quantity: number }) {
  return post<ResultadoTransferenciaBotiquin>('/api/hce/farmacia/botiquin/transferir', input);
}

// =============================================================================
// Facturación de Farmacia Interna (Fase 3, 2026-09-11) — Órdenes en 4 pasos,
// Comprobantes, Convenios y Reportes/Caja propios. Mismo motor que
// /admin/facturacion del CRM (ver http-routes/hce-farmacia-facturacion.ts),
// mismo Bearer (sesión de proxy) que el resto de este cliente.
// =============================================================================

export type LineaOrdenFarmacia = {
  id: number;
  itemId: number;
  description: string;
  quantity: number;
  unitAmountCents: number;
  ivaRate: number;
  lineTotalCents: number;
  /** `MedicationRequest/<id>` de Medplum si esta línea viene de una receta de la cola. */
  medicationRequestRef: string | null;
};
export type OrdenFarmacia = {
  id: number;
  /** Paciente (lead) dueño de la orden — el servidor ya lo persiste desde Fase 1a de la orden de cobro; `null` en órdenes sueltas de mostrador sin paciente identificado. */
  patientId: number | null;
  patientPhone: string;
  buyerIdType: BuyerIdTypeFarmacia;
  buyerIdNumber: string;
  status: 'draft' | 'signed' | 'invoiced' | 'cancelled';
  signedAt: string | null;
  createdAt: string;
  stockAplicado: boolean;
  totalCents: number;
  lines: LineaOrdenFarmacia[];
};

export async function listarOrdenesFarmacia(): Promise<{ disponible: boolean; orders: OrdenFarmacia[] }> {
  return get('/api/hce/farmacia/ordenes', { disponible: true, orders: [] });
}

export async function crearOrdenFarmacia(input: {
  patientPhone?: string;
  buyerIdType: BuyerIdTypeFarmacia;
  buyerIdNumber?: string;
  lines: { itemId: number; quantity: number; medicationRequestRef?: string }[];
  /** Paciente (lead) dueño de la orden — se persiste desde la creación, ver `OrdenFarmacia.patientId`. */
  patientId?: number;
}) {
  return post<{ id: number; totalCents: number }>('/api/hce/farmacia/ordenes', input);
}

export async function firmarOrdenFarmacia(orderId: number) {
  return post<{ ok: true }>(`/api/hce/farmacia/ordenes/${orderId}/firmar`, {});
}

export async function aplicarOrdenFarmacia(orderId: number) {
  return post<{ consumidas: { itemId: number; description: string; quantity: number }[] }>(
    `/api/hce/farmacia/ordenes/${orderId}/aplicar`,
    {},
  );
}

export type FormaPagoParticular = 'cash' | 'card' | 'transfer';

/** Mismo mapeo que `EstadoFactura` de `lib/estado-factura.ts` (server) — reusado tal cual, sin reinventar el texto ni el color. */
export type EstadoVisibleFacturaFarmacia = 'autorizada' | 'pendiente' | 'rechazada' | 'de_prueba' | 'anulada';
export type ToneEstadoFacturaFarmacia = 'verde' | 'ambar' | 'rojo' | 'gris';
export type EstadoFacturaFarmacia = {
  estado: EstadoVisibleFacturaFarmacia;
  etiqueta: string;
  descripcion: string;
  tono: ToneEstadoFacturaFarmacia;
  etiquetaPrueba: boolean;
  motivo: string | null;
  requiereRegularizar: boolean;
};
/** Mismo `FacturaResumen` que arma `lib/cobrar.ts:resumenDeFactura` (server) — `numero` es `null` mientras la bandeja no la numera. */
export type FacturaResumenFarmacia = {
  invoiceId: number | null;
  numero: string | null;
  claveAcceso: string | null;
  ambiente: string | null;
  estado: EstadoFacturaFarmacia | null;
};

export async function venderOrdenFarmacia(
  orderId: number,
  input: {
    contactName?: string;
    comoPaga?: 'particular' | 'copago' | 'reembolso' | 'carta';
    /** Solo cuando comoPaga es "particular" — efectivo/tarjeta/transferencia. */
    metodoParticular?: FormaPagoParticular;
    buyerName?: string;
    /** Sobrescribe la identificación congelada en la orden (`consumidor_final` provisional u otra) — necesario ante el tope de USD 50 sin cédula. */
    buyerIdType?: BuyerIdTypeFarmacia;
    buyerIdNumber?: string;
    /** Paciente (lead) dueño de este cobro, si se eligió uno al crear la orden. */
    patientId?: number;
  } = {},
) {
  return post<{
    totalCents: number;
    paymentId: number;
    invoiceId: number | null;
    /** Comprobante HTML provisional (Fase 3): mientras la factura no tiene número, es lo único que hay para imprimir/mostrar. */
    printableHtml: string;
    /** Estado visible real de la factura al momento de vender (PR 8.3) — nunca un mensaje fijo de éxito. */
    factura: FacturaResumenFarmacia;
    avisoDispensacion?: string;
  }>(`/api/hce/farmacia/ordenes/${orderId}/vender`, input);
}

/** «¿Ya la autorizaron?» del panel de resultado de una venta (PR 8.3) — botón «Actualizar» y sondeo automático. */
export async function actualizarEntregaFacturaFarmacia(invoiceId: number) {
  return post<{ factura: FacturaResumenFarmacia; limitado: boolean }>(`/api/hce/farmacia/comprobantes/${invoiceId}/actualizar-entrega`, {});
}

/** Cancela una orden que no llegó a "Vender" (draft/signed, incluso ya aplicada) — reintegra stock real si ya se había descontado. */
export async function cancelarOrdenFarmacia(orderId: number, motivo: string) {
  return post<{ ok: true }>(`/api/hce/farmacia/ordenes/${orderId}/cancelar`, { motivo });
}

/** Ruta de "Devolver venta" de esta factura (Fase 8.2) — mismo contrato que ya calcula el servidor para el CRM (lib/devolucion-venta.ts, `calcularRutaDevolucion`). `null` = factura sin emitir, nada que devolver todavía. */
export type RutaDevolucionFarmacia = 'dentro_de_plazo' | 'fuera_de_plazo' | 'bloqueada' | 'consumidor_final' | null;

export type FacturaFarmacia = {
  id: number;
  buyerName: string;
  buyerIdType: string;
  buyerIdNumber: string;
  totalCents: number;
  currency: string;
  status: string;
  createdAt: string;
  patientId: number | null;
  creditadoCents: number;
  saldoCents: number;
  bloqueadaPorNormaCF: boolean;
  esConsumidorFinal: boolean;
  anulacionEnCurso: boolean;
  ruta: RutaDevolucionFarmacia;
  vencePlazo: string | null;
  motivoBloqueo: string | null;
  /** Líneas de la factura SOLO si mezcla tarifas de IVA (editor «Detalle por líneas» de la nota); `null` = una tarifa: la pantalla no cambia. */
  lineas: { itemId: number | null; description: string; quantity: number; unitAmountCents: number; ivaRate: number }[] | null;
};

export async function listarComprobantesFarmacia(): Promise<{ disponible: boolean; facturas: FacturaFarmacia[] }> {
  return get('/api/hce/farmacia/comprobantes', { disponible: true, facturas: [] });
}

export async function anularComprobanteFarmacia(invoiceId: number, motivo: string) {
  return post<{ ok: true }>(`/api/hce/farmacia/comprobantes/${invoiceId}/anular`, { motivo });
}

// ---------------------------------------------------------------------------
// "Devolver venta" (Fase 8.2) — mismas 4 rutas reales que ya usa el CRM (routes/admin-billing-arcsa-notas.ts),
// wireadas acá por las rutas propias de Farmacia (http-routes/hce-farmacia-facturacion.ts).
// ---------------------------------------------------------------------------

export const FORMAS_REEMBOLSO_FARMACIA = ['efectivo', 'transferencia', 'otro'] as const;
export type FormaReembolsoFarmacia = (typeof FORMAS_REEMBOLSO_FARMACIA)[number];

export async function devolucionBloquearFarmacia(invoiceId: number, input: { bloqueada: boolean; motivo?: string | null }) {
  return post<{ ok: true }>(`/api/hce/farmacia/comprobantes/${invoiceId}/devolucion-bloquear`, input);
}

export type DevolucionCfResultadoFarmacia = { invoiceId: number; numeroConstancia: string; paymentId: number; orderId: number };

export async function devolucionConsumidorFinalFarmacia(invoiceId: number, input: { motivo: string; formaReembolso: FormaReembolsoFarmacia }) {
  return post<DevolucionCfResultadoFarmacia>(`/api/hce/farmacia/comprobantes/${invoiceId}/devolucion-consumidor-final`, input);
}

export type DatosAnularEnSriFarmacia = { claveAcceso: string; fechaEmision: string; fechaAutorizacion: string | null; tipo: string };
export type AnulacionEnLineaIniciarResultado = { ok: true; datos: DatosAnularEnSriFarmacia } | { ok: false; reason: string };

export async function anulacionEnLineaIniciarFarmacia(invoiceId: number): Promise<AnulacionEnLineaIniciarResultado> {
  const r = await post<AnulacionEnLineaIniciarResultado>(`/api/hce/farmacia/comprobantes/${invoiceId}/anulacion-en-linea-iniciar`, {});
  return r.ok ? r.datos : { ok: false, reason: r.error };
}

export type AnulacionEnLineaVerificarResultado =
  | { ok: true; accion: 'nada_que_verificar' }
  | { ok: true; accion: 'sigue_pendiente' }
  | { ok: true; accion: 'anulada'; reversaPagoFallida: boolean }
  | { ok: false; accion: 'transporte' }
  | { ok: false; accion: 'revision_manual'; motivo: string };

export async function anulacionEnLineaVerificarFarmacia(invoiceId: number): Promise<AnulacionEnLineaVerificarResultado> {
  const r = await post<AnulacionEnLineaVerificarResultado>(`/api/hce/farmacia/comprobantes/${invoiceId}/anulacion-en-linea-verificar`, {});
  return r.ok ? r.datos : { ok: false, accion: 'transporte' };
}

export type DevolucionNotaCreditoResultado = { ok: true; nota: { invoiceId: number | null; totalCents: number } } | { ok: false; reason: string };

export async function devolucionNotaCreditoFarmacia(invoiceId: number, motivo: string): Promise<DevolucionNotaCreditoResultado> {
  const r = await post<DevolucionNotaCreditoResultado>(`/api/hce/farmacia/comprobantes/${invoiceId}/devolucion-nota-credito`, { motivo });
  return r.ok ? r.datos : { ok: false, reason: r.error };
}

export type LineaComprobanteFarmacia = {
  itemId: number | null;
  description: string;
  quantity: number;
  unitAmountCents: number;
  ivaRate: number;
};
export type NotaEmitidaFarmacia = { invoiceId: number | null; sriDocumentId: number | null; totalCents: number; printableHtml: string };

export async function emitirNotaCreditoFarmacia(invoiceId: number, input: { montoCents?: number; motivo: string; lines?: LineaComprobanteFarmacia[] }) {
  return post<NotaEmitidaFarmacia>(`/api/hce/farmacia/comprobantes/${invoiceId}/nota-credito`, input);
}

export async function emitirNotaDebitoFarmacia(invoiceId: number, input: { montoCents: number; motivo: string; lines?: LineaComprobanteFarmacia[] }) {
  return post<NotaEmitidaFarmacia>(`/api/hce/farmacia/comprobantes/${invoiceId}/nota-debito`, input);
}

export async function descargarComprobanteFarmacia(invoiceId: number, tipo: 'ride' | 'xml') {
  return post<{ contentType: string; filename: string; content: string }>(`/api/hce/farmacia/comprobantes/${invoiceId}/descargar`, { tipo });
}

export type ConvenioFarmacia = {
  id: number;
  code: string;
  name: string;
  ruc: string | null;
  requierePrefactura: boolean;
  active: boolean;
  puedeFacturar: boolean;
};

export async function listarConveniosFarmacia(): Promise<{ disponible: boolean; convenios: ConvenioFarmacia[] }> {
  return get('/api/hce/farmacia/convenios', { disponible: true, convenios: [] });
}

export async function actualizarConvenioFarmacia(convenioId: number, cambios: { ruc?: string | null; active?: boolean }) {
  return patch<{ id: number }>(`/api/hce/farmacia/convenios/${convenioId}`, cambios);
}

export type FacturaConvenioFarmacia = {
  invoiceId: number;
  convenioId: number | null;
  convenioName: string;
  buyerIdNumber: string;
  totalCents: number;
  currency: string;
  origenRef: string | null;
  createdAt: string;
};

export async function listarFacturasConvenioFarmacia(): Promise<{ disponible: boolean; facturas: FacturaConvenioFarmacia[] }> {
  return get('/api/hce/farmacia/convenios/facturas', { disponible: true, facturas: [] });
}

export async function facturarConvenioFarmacia(input: {
  paymentId: number;
  convenioId: number;
  coveredAmountCents: number;
  paciente: string;
  poliza?: string;
  autorizacion?: string;
  prefacturaRef?: string;
  copagoCents?: number;
  lines?: LineaComprobanteFarmacia[];
}) {
  return post<{ invoiceId: number | null; totalCents: number; convenioName: string }>('/api/hce/farmacia/convenios/facturar', input);
}

export type CajaItemFarmacia = { paymentId: number; contactName: string | null; amountCents: number; currency: string; method: string; comoPaga: string; reason: string | null; at: string | null };
export type CajaPendienteFarmacia = { tipo: 'venta_farmacia'; ref: string; contactName: string; amountCents: number | null; at: string | null; nota: string; facturadoConvenio: boolean };
export type CajaDiaFarmacia = { cobradoHoyCents: number; items: CajaItemFarmacia[]; porCobrarConvenio: CajaPendienteFarmacia[]; nota: string };

export async function cajaDelDiaFarmacia(): Promise<CajaDiaFarmacia> {
  return get('/api/hce/farmacia/caja/dia', { cobradoHoyCents: 0, items: [], porCobrarConvenio: [], nota: '' });
}

export type ReporteVentasFarmacia = {
  desde: string;
  hasta: string;
  disponibleFacturacion: boolean;
  cobrado: { totalCents: number; cantidad: number; porMetodo: { metodo: string; label: string; totalCents: number; cantidad: number }[] };
  facturado: { totalCents: number; cantidad: number; alPacienteCents: number; alPacienteCantidad: number; alConvenioCents: number; alConvenioCantidad: number };
  iva: { porTarifa: { ivaRate: number; baseCents: number; ivaCalculadoCents: number; lineas: number }[]; ivaCobradoCents: number; huecoIva: boolean; baseNoCeroCents: number };
  porDia: { fecha: string; cobradoCents: number; facturadoCents: number }[];
};

export async function reporteVentasFarmacia(desde?: string, hasta?: string): Promise<ReporteVentasFarmacia | null> {
  const qs = new URLSearchParams();
  if (desde) qs.set('desde', desde);
  if (hasta) qs.set('hasta', hasta);
  const query = qs.toString();
  const res = await fetch(vaultUrl(`/api/hce/farmacia/reportes/ventas${query ? `?${query}` : ''}`), { headers: cabecerasBoveda() });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}
